import { Redirect, Route } from 'react-router-dom';
import {
  IonApp, IonIcon, IonLabel, IonRouterOutlet,
  IonTabBar, IonTabButton, IonTabs, setupIonicReact
} from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { calculatorOutline, bookmarkOutline, settingsOutline } from 'ionicons/icons';
import { useEffect, useRef, useState } from 'react';
import '@khmyznikov/pwa-install';

// Import custom interface for PWA Install element
import { PWAInstallElement } from './env';

/* Import pages */
import Calculator from './pages/Calculator';
import SavedCalculations from './pages/SavedCalculations';
import Settings from './pages/Settings';
import UpdateAlert from './components/UpdateAlert';
import DesktopLanding from './components/DesktopLanding';
import { isMobileDevice } from './utils/deviceUtils';
import { useTheme } from './hooks/themeHooks';
import { PWA_INSTALL_DELAY } from './utils/constants';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

/* Ionic Dark Mode */
import '@ionic/react/css/palettes/dark.class.css';

/* Theme variables */
import './theme/variables.css';

setupIonicReact();

const App: React.FC = () => {
  const [isMobile, setIsMobile] = useState(true);
  const pwaInstallRef = useRef<PWAInstallElement | null>(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  
  // Initialize theme - this will handle applying the correct theme on load
  useTheme();
  
  useEffect(() => {
    // Check if device is mobile on component mount
    setIsMobile(isMobileDevice());

    // Check if app is running on localhost
    const isLocalhost = window.location.hostname === 'localhost' || 
                       window.location.hostname === '127.0.0.1';

    // Check if app is running in standalone mode
    const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                      (window.navigator as any).standalone === true ||
                      isLocalhost;
    setIsStandalone(standalone);
    
    // Add resize listener to update if window size changes
    const handleResize = () => {
      setIsMobile(isMobileDevice());
    };
    
    window.addEventListener('resize', handleResize);

    // Show installation dialog if not in standalone mode
    if (!standalone && pwaInstallRef.current) {
      // Use a small timeout to ensure component is fully initialized
      const timeoutId = setTimeout(() => {
        if (pwaInstallRef.current) {
          pwaInstallRef.current.showDialog();
          setShowInstallPrompt(true);
        }
      }, PWA_INSTALL_DELAY);
      
      // Add event listeners for installation events
      const handleSuccess = (e: any) => {
        console.log('Installation successful:', e.detail.message);
        setShowInstallPrompt(false);
      };
      
      const handleUserChoice = (e: any) => {
        console.log('User choice:', e.detail.message);
      };
      
      pwaInstallRef.current.addEventListener('pwa-install-success-event', handleSuccess);
      pwaInstallRef.current.addEventListener('pwa-user-choice-result-event', handleUserChoice);
      
      // Clean up listeners and timeout when component unmounts
      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener('resize', handleResize);
        if (pwaInstallRef.current) {
          pwaInstallRef.current.removeEventListener('pwa-install-success-event', handleSuccess);
          pwaInstallRef.current.removeEventListener('pwa-user-choice-result-event', handleUserChoice);
        }
      };
    }
    
    // Clean up resize listener if no PWA listeners were added
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // If not mobile, show the desktop landing page
  if (!isMobile) {
    return <DesktopLanding />;
  }

  // Otherwise show the regular mobile app
  return (
    <>
      {!isStandalone && (
        <>
          {showInstallPrompt && (
            <div
              style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0,0,0,0.7)',
                zIndex: 9998,
                backdropFilter: 'blur(2px)',
                transition: 'opacity 0.3s ease',
              }}
            />
          )}
          <pwa-install
            ref={pwaInstallRef}
            manual-apple="true"
            manual-chrome="true"
            disable-screenshots-chrome="true"
            disable-close
            use-local-storage
            install-description="Install this tool to unlock the full experience"
            style={{ position: 'fixed', inset: 0, background: 'transparent', zIndex: 9999 }}
          />
        </>
      )}
      <IonApp className={`${showInstallPrompt ? 'app-dimmed' : ''}`}>
        <UpdateAlert />
        <IonReactRouter>
          <IonTabs>
            <IonRouterOutlet>
              <Route exact path="/calculator">
                <Calculator />
              </Route>
              <Route exact path="/saved">
                <SavedCalculations />
              </Route>
              <Route path="/settings">
                <Settings />
              </Route>
              <Route exact path="/">
                <Redirect to="/calculator" />
              </Route>
            </IonRouterOutlet>
            <IonTabBar slot="bottom">
              <IonTabButton tab="calculator" href="/calculator">
                <IonIcon aria-hidden="true" icon={calculatorOutline} />
                <IonLabel>Calculator</IonLabel>
              </IonTabButton>
              <IonTabButton tab="saved" href="/saved">
                <IonIcon aria-hidden="true" icon={bookmarkOutline} />
                <IonLabel>Saved</IonLabel>
              </IonTabButton>
              <IonTabButton tab="settings" href="/settings">
                <IonIcon aria-hidden="true" icon={settingsOutline} />
                <IonLabel>Settings</IonLabel>
              </IonTabButton>
            </IonTabBar>
          </IonTabs>
        </IonReactRouter>
      </IonApp>
    </>
  );
};

export default App;
